import { Component, type ErrorInfo, type ReactNode } from "react";
import { analytics } from "@/lib/observability";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * Application-level error boundary. Bridges React errors into:
 *   - analytics.track('error_boundary_triggered')  → essential sinks only
 *   - analytics.captureException(err)              → Sentry with component stack
 */
export class AnalyticsErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, message: err.message || "Unknown error" };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    const route = typeof window !== "undefined" ? window.location.pathname : "unknown";
    const component = this.props.componentName || "RootBoundary";
    try {
      analytics.track("error_boundary_triggered", {
        component,
        route,
        message: (err.message || "Unknown error").slice(0, 500),
      });
      analytics.captureException(err, {
        component,
        route,
        componentStack: info.componentStack ?? null,
      });
    } catch {
      /* never throw from analytics */
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="min-h-[40vh] flex flex-col items-center justify-center p-8 text-center">
            <h2 className="text-xl font-semibold mb-2">Algo deu errado</h2>
            <p className="text-muted-foreground text-sm mb-4 max-w-md">
              Atualize a página. Se o problema persistir, nosso suporte já foi notificado.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm"
            >
              Recarregar
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
